import { type MatterSettings } from "@/lib/data/requests/matters/matter-settings";
import {
  createMatterTaskPlanTaskV1,
  createMatterTaskPlanV1,
  deleteMatterTaskPlanTaskV1,
  deleteMatterTaskPlanV1,
  retrieveMatterTaskPlans,
  type RetrieveMatterTaskPlansParams,
  updateMatterTaskPlanTaskV1,
  updateMatterTaskPlanV1,
} from "@/lib/data/requests/matters/matter-task-plan";
import { queryKeys } from "@/lib/data/static/query-keys";
import { type APIError, type APIResponse } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";

// Matter Task Plan Queries

export const useCreateMatterTaskPlan = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Parameters<typeof createMatterTaskPlanV1>[0]) => {
      return createMatterTaskPlanV1(data);
    },
    onMutate: (data) => {
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;
          return {
            data: {
              ...oldData.data,
              taskPlans: [...oldData.data.taskPlans, data],
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.matterSettings]),
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      if (showToast) {
        toastAPIErrorMessage(error, "Error creating matter task plan");
      }
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeys.matterSettings],
        }),
        queryClient.invalidateQueries({
          queryKey: [queryKeys.matterTaskPlans],
        }),
      ]);
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("Matter task plan created successfully");
      }
    },
  });
};

export const useUpdateMatterTaskPlan = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Parameters<typeof updateMatterTaskPlanV1>[0]) => {
      return updateMatterTaskPlanV1(data);
    },
    onMutate: (data) => {
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;
          return {
            data: {
              ...oldData.data,
              taskPlans: oldData.data.taskPlans.map((item) =>
                item.id === data.id ? { ...item, ...data } : item,
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.matterSettings]),
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      if (showToast) {
        toastAPIErrorMessage(error, "Error updating matter task plan");
      }
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeys.matterSettings],
        }),
        queryClient.invalidateQueries({
          queryKey: [queryKeys.matterTaskPlans],
        }),
      ]);
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("Matter task plan updated successfully");
      }
    },
  });
};

export const useDeleteMatterTaskPlan = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Parameters<typeof deleteMatterTaskPlanV1>[0]) => {
      return deleteMatterTaskPlanV1(data);
    },
    onMutate: (data) => {
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;
          return {
            data: {
              ...oldData.data,
              taskPlans: oldData.data.taskPlans.filter(
                (item) => item.id !== data.id,
              ),
            },
          };
        },
      );
      return {
        previousData: queryClient.getQueryData([queryKeys.matterSettings]),
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      if (showToast) {
        toastAPIErrorMessage(error, "Error deleting matter task plan");
      }
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeys.matterSettings],
        }),
        queryClient.invalidateQueries({
          queryKey: [queryKeys.matterTaskPlans],
        }),
      ]);
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("Matter task plan deleted successfully");
      }
    },
  });
};

export const retrieveMatterTaskPlansQueryOptions = (
  filters: RetrieveMatterTaskPlansParams = {},
) =>
  queryOptions({
    queryKey: [queryKeys.matterTaskPlans, filters],
    queryFn: async () => {
      return retrieveMatterTaskPlans(filters);
    },
    placeholderData: keepPreviousData,
  });

export const useCreateMatterTaskPlanTask = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      data: Parameters<typeof createMatterTaskPlanTaskV1>[0],
    ) => {
      return createMatterTaskPlanTaskV1(data);
    },
    onError: (error: AxiosError<APIError>) => {
      if (showToast) {
        toastAPIErrorMessage(error, "Error creating task plan task");
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matterTaskPlans],
      });
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("Task plan task created successfully");
      }
    },
  });
};

export const useUpdateMatterTaskPlanTask = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      data: Parameters<typeof updateMatterTaskPlanTaskV1>[0],
    ) => {
      return updateMatterTaskPlanTaskV1(data);
    },
    onError: (error: AxiosError<APIError>) => {
      if (showToast) {
        toastAPIErrorMessage(error, "Error updating task plan task");
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matterTaskPlans],
      });
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("Task plan task updated successfully");
      }
    },
  });
};

export const useDeleteMatterTaskPlanTask = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      data: Parameters<typeof deleteMatterTaskPlanTaskV1>[0],
    ) => {
      return deleteMatterTaskPlanTaskV1(data);
    },
    onError: (error: AxiosError<APIError>) => {
      if (showToast) {
        toastAPIErrorMessage(error, "Error deleting task plan task");
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matterTaskPlans],
      });
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("Task plan task deleted successfully");
      }
    },
  });
};
